import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useBusiness } from "@/hooks/use-business";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Calendar as CalendarIcon, Copy, ExternalLink, Loader2, PartyPopper, Settings } from "lucide-react";
import { format, parseISO } from "date-fns";
import { getBaseUrl } from "@/lib/site";

function copyToClipboard(text: string): Promise<boolean> {
  const s = String(text || "").trim();
  if (!s) return Promise.resolve(false);
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(s).then(() => true).catch(() => false);
  }
  try {
    const el = document.createElement("textarea");
    el.value = s;
    el.setAttribute("readonly", "true");
    el.style.position = "fixed";
    el.style.top = "0";
    el.style.left = "0";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.focus();
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    return Promise.resolve(true);
  } catch {
    return Promise.resolve(false);
  }
}

export default function CateringRequestsPage() {
  const { business, loading: businessLoading } = useBusiness();
  const { toast } = useToast();
  const sb = supabase as any;

  const primary = ((business as any)?.primary_module || "appointments") as string;
  const isCatering = primary === "catering_services" || primary === "food_vendors";
  const eventCateringEnabled = isCatering && (business as any)?.event_catering_requests_enabled === true;
  const cateringRequestUrl =
    eventCateringEnabled && (business as any)?.slug
      ? `${getBaseUrl()}/catering/${(business as any).slug}/request`
      : "";

  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requests, setRequests] = useState<any[]>([]);
  const [editRequestOpen, setEditRequestOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<any | null>(null);
  const [editReq, setEditReq] = useState<any>({
    company_name: "",
    event_date: "",
    attendees: "0",
    budget_amount: "",
    service_style: "buffet",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    location: "",
    notes: "",
    status: "new",
  });

  const fetchRequests = async () => {
    if (!business?.id) return;
    setRequestsLoading(true);
    try {
      const { data, error } = await sb
        .from("catering_requests")
        .select("*")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      setRequests(data || []);
    } catch (e: any) {
      toast({ title: "Failed to load requests", description: e?.message || "Try again.", variant: "destructive" });
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  };

  useEffect(() => {
    if (business?.id && eventCateringEnabled) fetchRequests();
  }, [business?.id, eventCateringEnabled]);

  const updateRequestStatus = async (id: string, status: string) => {
    try {
      const { error } = await sb.from("catering_requests").update({ status }).eq("id", id);
      if (error) throw error;
      await fetchRequests();
    } catch (e: any) {
      toast({ title: "Failed to update status", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  const createInvoiceFromRequest = async (r: any) => {
    if (!business?.id) return;
    try {
      const currency = (business as any)?.currency || "GHS";
      const billToName = String(r.company_name || r.contact_name || "Client");
      const issueDate = new Date().toISOString().slice(0, 10);
      const budget = r.budget_amount != null ? Number(r.budget_amount) : null;
      const attendees = Number(r.attendees || 0);
      const eventDate = r.event_date ? String(r.event_date) : "";
      const style = String(r.service_style || "buffet");
      const notesParts = [
        budget != null ? `Budget: ${currency} ${budget}` : "",
        attendees ? `Attendees: ${attendees}` : "",
        eventDate ? `Event date: ${eventDate}` : "",
        style ? `Service style: ${style}` : "",
        r.location ? `Location: ${r.location}` : "",
      ].filter(Boolean);
      const invPayload: any = {
        business_id: business.id,
        customer_id: null,
        status: "draft",
        currency,
        issue_date: issueDate,
        due_date: null,
        payment_terms: null,
        bill_to_name: billToName,
        bill_to_email: r.contact_email || null,
        bill_to_phone: r.contact_phone || null,
        notes: (notesParts.join("\n") || null) as any,
        terms: null,
      };
      const { data: created, error: cErr } = await sb.from("invoices").insert(invPayload).select("*").single();
      if (cErr) throw cErr;
      const qty = Math.max(1, attendees || 0);
      const unitPrice = budget != null && qty > 0 ? budget / qty : 0;
      const desc = `Catering (${style}) • per guest${eventDate ? ` • ${eventDate}` : ""}`;
      await sb.from("invoice_items").insert({
        business_id: business.id,
        invoice_id: created.id,
        position: 1,
        description: desc,
        quantity: qty,
        unit_price: unitPrice,
      });
      await sb.from("catering_requests").update({ invoice_id: created.id, status: "invoiced" }).eq("id", r.id);
      toast({ title: "Draft invoice created" });
      await fetchRequests();
      window.open(`${getBaseUrl()}/app/invoicing?edit=${created.id}`, "_blank");
    } catch (e: any) {
      toast({ title: "Failed to create invoice", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  const openEditRequest = (r: any) => {
    setEditingRequest(r);
    setEditReq({
      company_name: r.company_name || "",
      event_date: r.event_date || "",
      attendees: String(r.attendees ?? 0),
      budget_amount: r.budget_amount != null ? String(r.budget_amount) : "",
      service_style: r.service_style || "buffet",
      contact_name: r.contact_name || "",
      contact_phone: r.contact_phone || "",
      contact_email: r.contact_email || "",
      location: r.location || "",
      notes: r.notes || "",
      status: r.status || "new",
    });
    setEditRequestOpen(true);
  };

  const saveEditedRequest = async () => {
    if (!editingRequest?.id) return;
    try {
      const payload: any = {
        company_name: editReq.company_name?.trim() || null,
        event_date: editReq.event_date?.trim() || null,
        attendees: Math.max(0, Number(editReq.attendees || 0)),
        budget_amount: editReq.budget_amount?.trim() ? Number(editReq.budget_amount) : null,
        service_style: String(editReq.service_style || "buffet"),
        contact_name: String(editReq.contact_name || "").trim(),
        contact_phone: editReq.contact_phone?.trim() || null,
        contact_email: editReq.contact_email?.trim() || null,
        location: editReq.location?.trim() || null,
        notes: editReq.notes?.trim() || null,
        status: String(editReq.status || "new"),
      };
      if (!payload.contact_name) {
        toast({ title: "Contact name is required", variant: "destructive" });
        return;
      }
      const { error } = await sb.from("catering_requests").update(payload).eq("id", editingRequest.id);
      if (error) throw error;
      toast({ title: "Request updated" });
      setEditRequestOpen(false);
      setEditingRequest(null);
      await fetchRequests();
    } catch (e: any) {
      toast({ title: "Failed to save request", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  if (businessLoading || !business) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <div className="flex items-center justify-center min-h-[40vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  if (!isCatering) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <PageHeader title="Catering requests" description="Event and buffet request form and list" />
        <Card className="border border-border">
          <CardContent className="p-8 text-center text-muted-foreground">
            <p>This page is for catering and food-vendor businesses. Switch your business module in Settings to use it.</p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/app/settings">Settings</Link>
            </Button>
          </CardContent>
        </Card>
      </AppShell>
    );
  }

  if (!eventCateringEnabled) {
    return (
      <AppShell businessName={business?.name || "My Business"}>
        <PageHeader title="Catering requests" description="Event and buffet request form and list" />
        <Card className="border border-border">
          <CardContent className="p-8 text-center">
            <PartyPopper className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              Enable the event / catering request form in Settings to accept requests and see them here.
            </p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/app/settings">
                <Settings className="h-4 w-4 mr-2" />
                Open Settings
              </Link>
            </Button>
          </CardContent>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Catering requests"
        description="Share the request form link and manage event or buffet requests"
      />

      <div className="space-y-6">
        <Card className="border border-border">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className="text-sm text-muted-foreground">Public request form link</Label>
              <div className="flex items-center gap-2 mt-2 bg-muted/50 rounded-lg border border-border p-3">
                <input
                  type="text"
                  value={cateringRequestUrl || ""}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-foreground outline-none min-w-0"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    const ok = await copyToClipboard(cateringRequestUrl || "");
                    if (ok) {
                      toast({ title: "Link copied", description: "Request form link copied to clipboard" });
                    } else {
                      toast({ title: "Copy failed", description: "Please copy the link manually.", variant: "destructive" });
                    }
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" asChild>
                  <a href={cateringRequestUrl || "#"} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Share this link so customers can submit event or buffet catering requests.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchRequests} disabled={requestsLoading}>
              {requestsLoading ? "Refreshing..." : "Refresh list"}
            </Button>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <h2 className="font-semibold text-foreground mb-4">Requests</h2>

            <div className="sm:hidden space-y-3">
              {requests.length === 0 ? (
                <div className="rounded-xl border border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
                  No requests yet. Share the form link to receive event or buffet requests.
                </div>
              ) : (
                requests.map((r: any) => (
                  <div key={r.id} className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{r.company_name || r.contact_name || "Request"}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {r.event_date ? `Event • ${r.event_date}` : "Event • —"} • {r.service_style || "buffet"} •{" "}
                          {Number(r.attendees || 0)} pax
                        </div>
                      </div>
                      <div className="text-xs font-medium capitalize">{String(r.status || "new").replace(/_/g, " ")}</div>
                    </div>
                    {r.notes ? <div className="text-sm text-muted-foreground line-clamp-3">{r.notes}</div> : null}
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      <Button variant="outline" className="w-full" size="sm" onClick={() => openEditRequest(r)}>
                        Edit
                      </Button>
                      <Button
                        className="w-full"
                        size="sm"
                        onClick={() => createInvoiceFromRequest(r)}
                        disabled={!!r.invoice_id}
                      >
                        {r.invoice_id ? "Invoiced" : "Create invoice"}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="hidden sm:block rounded-xl border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company / Contact</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Style</TableHead>
                    <TableHead className="text-right">Pax</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-sm text-muted-foreground text-center py-8">
                        No requests yet. Share the form link to receive event or buffet requests.
                      </TableCell>
                    </TableRow>
                  ) : (
                    requests.map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell className="min-w-[200px]">
                          <div className="font-medium">{r.company_name || r.contact_name || "Request"}</div>
                          <div className="text-xs text-muted-foreground">
                            {[r.contact_email, r.contact_phone].filter(Boolean).join(" • ") || "—"}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{r.event_date || "—"}</TableCell>
                        <TableCell className="text-sm capitalize">{String(r.service_style || "buffet")}</TableCell>
                        <TableCell className="text-right">{Number(r.attendees || 0)}</TableCell>
                        <TableCell className="text-sm capitalize">{String(r.status || "new").replace(/_/g, " ")}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => openEditRequest(r)}>
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => createInvoiceFromRequest(r)}
                              disabled={!!r.invoice_id}
                            >
                              {r.invoice_id ? "Invoiced" : "Create invoice"}
                            </Button>
                            <Select value={String(r.status || "new")} onValueChange={(v) => updateRequestStatus(r.id, v)}>
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {["new", "contacted", "quoted", "invoiced", "confirmed", "delivered", "closed", "cancelled"].map(
                                  (s) => (
                                    <SelectItem key={s} value={s}>
                                      {s.replace(/_/g, " ")}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={editRequestOpen}
        onOpenChange={(v) => {
          setEditRequestOpen(v);
          if (!v) setEditingRequest(null);
        }}
      >
        <DialogContent className="sm:max-w-[720px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Edit request</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="grid gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Company name</Label>
                  <Input
                    value={editReq.company_name || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, company_name: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select value={String(editReq.status || "new")} onValueChange={(v) => setEditReq((x: any) => ({ ...x, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["new", "contacted", "quoted", "invoiced", "confirmed", "delivered", "closed", "cancelled"].map((s) => (
                        <SelectItem key={s} value={s}>
                          {s.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Event date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between">
                        <span className={editReq.event_date ? "text-foreground" : "text-muted-foreground"}>
                          {editReq.event_date ? format(parseISO(editReq.event_date), "MMM d, yyyy") : "Select a date"}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editReq.event_date ? parseISO(editReq.event_date) : undefined}
                        onSelect={(d) => {
                          if (!d) {
                            setEditReq((x: any) => ({ ...x, event_date: "" }));
                            return;
                          }
                          setEditReq((x: any) => ({ ...x, event_date: format(d, "yyyy-MM-dd") }));
                        }}
                        initialFocus
                        className="p-2"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label>Attendees</Label>
                  <Input
                    type="number"
                    min={0}
                    value={String(editReq.attendees ?? "0")}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, attendees: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Budget</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editReq.budget_amount || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, budget_amount: e.target.value }))}
                    placeholder="e.g., 1500"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Service style</Label>
                  <Select
                    value={String(editReq.service_style || "buffet")}
                    onValueChange={(v) => setEditReq((x: any) => ({ ...x, service_style: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buffet">Buffet</SelectItem>
                      <SelectItem value="packed">Packed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Contact name</Label>
                  <Input
                    value={editReq.contact_name || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, contact_name: e.target.value }))}
                    placeholder="Required"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input
                    value={editReq.contact_phone || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, contact_phone: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editReq.contact_email || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, contact_email: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Location</Label>
                  <Input
                    value={editReq.location || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, location: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Textarea
                  value={editReq.notes || ""}
                  onChange={(e) => setEditReq((x: any) => ({ ...x, notes: e.target.value }))}
                  placeholder="Dietary notes, delivery constraints, preferences, etc."
                  className="min-h-28 resize-none"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            {editingRequest?.invoice_id ? (
              <Button
                variant="outline"
                className="flex-1 rounded-lg"
                onClick={() =>
                  window.open(`${getBaseUrl()}/app/invoicing?edit=${editingRequest.invoice_id}`, "_blank")
                }
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open invoice
              </Button>
            ) : (
              <Button variant="outline" className="flex-1 rounded-lg" onClick={() => setEditRequestOpen(false)}>
                Cancel
              </Button>
            )}
            <Button className="flex-1 rounded-lg" onClick={saveEditedRequest}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
